/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { get } from 'lodash';
import isEqual from 'lodash/isEqual';
import defaultsDeep from 'lodash/defaultsDeep';
import { compose } from 'lodash/fp';
import createButtonCss from '../Button';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { processButtonData } from '../../../utils/analytics';
import getComponent from '../../../utils/getComponent';
import { COMPONENT_NAMES } from '../../../utils/constants';
import { modalIds, ModalsConsumer } from '../../../contexts/ModalsContext';
import LinkWrapper from '../LinkWrapper';

const defaultData = {
  linkObject: {
    value: {
      href: '',
      text: '',
      target: '',
    },
  },
  prefilledValues: {},
  customCss: {},
  componentName: '',
  t: key => key,
};

export class OpenEnquiryFormButton extends SafePureComponent {
  fields = defaultsDeep(this.props, defaultData);

  openModalHandler = (event, openModal) => {
    event.preventDefault();

    openModal(modalIds.enquiry, this.fields.prefilledValues);
  };

  componentDidUpdate(prevProps) {
    if (isEqual(prevProps, this.props)) {
      return;
    }

    this.fields = defaultsDeep(this.props, prevProps);
  }

  getLocationIDFromContext = () => {
    const enquiryFields = getComponent(
      this.props.sitecoreContext,
      COMPONENT_NAMES.enquiryModal,
    );

    const locationIDValue = get(
      enquiryFields,
      'fields["LocationID"].value',
      '',
    );

    return locationIDValue;
  };

  safeRender() {
    const { linkObject, prefilledValues, customCss } = this.fields;
    const { href, text } = linkObject.value;
    const renderExternalLink = !!href && href !== 'http://';
    const modalButtonText = text || this.props.t('GetInTouchCTA');
    const { componentName } = this.props;

    if (renderExternalLink) {
      /**
       * If Enquiry is going to be opened in a new window, we need to store form values only
       * accessible at this point like already user prefilled values and the location ID
       */
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          'LocationID',
          this.getLocationIDFromContext(),
        );

        Object.entries(prefilledValues).forEach(([key, value]) => {
          window.sessionStorage.setItem(key, value);
        });
      }

      // It is required to wrap the LinkWrapper inside a div because otherwise it fails on deployed environments
      return (
        <div css={customCss}>
          <LinkWrapper
            field={linkObject}
            componentName={componentName}
            isGTMTrackable
          />
        </div>
      );
    }

    return (
      <ModalsConsumer>
        {({ openModal }) => (
          <button
            onClick={e => {
              processButtonData(componentName, modalButtonText);
              this.openModalHandler(e, openModal);
            }}
            css={[createButtonCss(), customCss]}
          >
            {modalButtonText}
          </button>
        )}
      </ModalsConsumer>
    );
  }
}

OpenEnquiryFormButton.displayName = 'OpenEnquiryFormButton';

OpenEnquiryFormButton.defaultProps = {
  ...defaultData,
};

OpenEnquiryFormButton.propTypes = {
  fields: PropTypes.shape({
    componentName: PropTypes.string,
    prefilledValues: PropTypes.object,
    customCss: PropTypes.object,
    linkObject: PropTypes.shape({
      value: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        text: PropTypes.string,
      }),
    }),
  }),
  t: PropTypes.func,
};

export default compose(
  withNamespaces(),
  withSitecoreContext(),
)(OpenEnquiryFormButton);
